import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import ProtectedRoute from './Components/ProtectedRoute';
import RoleGuard from './Components/RoleGuard';
import Layout from './Components/Layout';
import ErrorBoundary from './Components/ErrorBoundary';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ResetPassword from './pages/auth/ResetPassword';
import OnboardingForm from './pages/onboarding/OnboardingForm';
import OnboardingGenerator from './pages/onboarding/OnboardingGenerator';
import OnboardingReview from './pages/onboarding/OnboardingReview';
import OnboardingRequests from './pages/onboarding/OnboardingRequests';
import MoUSignPage from './pages/mou/MoUSignPage';

// Dashboard Pages
import AdminDashboard from './pages/dashboard/AdminDashboard';
import DoctorDashboard from './pages/dashboard/DoctorDashboard';
import HospitalDashboard from './pages/dashboard/HospitalDashboard';
import CustomerCareDashboard from './pages/dashboard/CustomerCareDashboard';
import MarketingAgentDashboard from './pages/dashboard/MarketingAgentDashboard';
import AccountantDashboard from './pages/dashboard/AccountantDashboard';
import CCHeadDashboard from './pages/dashboard/CCHeadDashboard';
import RoleFlowDashboard from './pages/dashboard/RoleFlowDashboard';
import GuidebookUI from './pages/dashboard/GuidebookUI';

// Lead Pages
import LeadList from './pages/leads/LeadList';
import LeadCreate from './pages/leads/LeadCreate';
import LeadRegistrationCC from './pages/leads/LeadRegistrationCC';
import LeadDetail from './pages/leads/LeadDetail';
import ReportsList from './pages/leads/ReportsList';
import PerformanceReports from './pages/leads/PerformanceReports';
// import ClinicalSubmission from './pages/public/ClinicalSubmission';
import ClinicalSubmission from './pages/public/ClinicalSubmission';
import HospitalClinicalSubmission from './pages/public/HospitalClinicalSubmission';
import DoctorReview from './pages/public/DoctorReview';
import PaymentStatus from './pages/public/PaymentStatus';
import SurgeryVerify from './pages/public/SurgeryVerify';
import DoctorAvailabilityPublic from './pages/public/DoctorAvailabilityPublic';
import MoUConsent from './pages/onboarding/MoUConsent';

// Surgery Pages
import SurgeryList from './pages/surgeries/SurgeryList';
import SurgeryDetail from './pages/surgeries/SurgeryDetail';
import SurgeryCaseDashboard from './pages/surgeries/SurgeryCaseDashboard';

// Appointment Pages
import AppointmentList from './pages/appointments/AppointmentList';
import AppointmentDetail from './pages/appointments/AppointmentDetail';
import AppointmentCreate from './pages/appointments/AppointmentCreate';
import AppointmentFollowUpList from './pages/appointments/AppointmentFollowUpList';
import PrescriptionFormPublic from './pages/public/PrescriptionFormPublic';
import PrescriptionList from './pages/prescriptions/PrescriptionList';
import PrescriptionDetail from './pages/prescriptions/PrescriptionDetail';
import PrescriptionCreate from './pages/prescriptions/PrescriptionCreate';
import CouponList from './pages/coupons/CouponList';
import TestimonialList from './pages/testimonials/TestimonialList';

// Payment Pages
import PaymentList from './pages/payments/PaymentList';
import PaymentDetail from './pages/payments/PaymentDetail';


// Follow-up Pages
import FollowUpList from './pages/followups/FollowUpList';
import FollowUpDetail from './pages/followups/FollowUpDetail';

// Payout Pages
import PayoutList from './pages/payouts/PayoutList';
import PayoutDetail from './pages/payouts/PayoutDetail';
import AccountantPayoutDashboard from './pages/payouts/AccountantPayoutDashboard';

// User Management Pages
import UserList from './pages/users/UserList';
import UserDetail from './pages/users/UserDetail';
import RoleManagement from './pages/users/RoleManagement';

// Exotel Calls
import CallAnalytics from './pages/calls/CallAnalytics';

// Doctor Pages
import DoctorList from './pages/doctors/DoctorList';
import DoctorDetail from './pages/doctors/DoctorDetail';

// Hospital Pages
import HospitalList from './pages/hospitals/HospitalList';
import HospitalDetail from './pages/hospitals/HospitalDetail';
import HospitalRateMaster from './pages/hospitals/HospitalRateMaster';

// Lead Partner Pages
import MarketingAgentList from './pages/marketingAgents/MarketingAgentList';
import MarketingAgentDetail from './pages/marketingAgents/MarketingAgentDetail';

// OTP Pages
import OTPList from './pages/otp/OTPList';
import OTPDetail from './pages/otp/OTPDetail';
import OTPGenerate from './pages/otp/OTPGenerate';
import ActiveOTPVerifications from './pages/otp/ActiveOTPVerifications';

// Doctor Availability Pages
import DoctorAvailability from './pages/doctorAvailability/DoctorAvailability';
import AvailabilityForecast from './pages/doctorAvailability/AvailabilityForecast';

// Enquiry Pages
import EnquiryForm from './pages/enquiry/EnquiryForm';

// Profile Page
import Profile from './pages/Profile';
import DoxezSupport from './pages/support/DoxezSupport';
import ScheduleAppointment from './pages/appointments/ScheduleAppointment';

// Admin Pages
import OnboardingManagement from './pages/admin/OnboardingManagement';
import SystemLogs from './pages/admin/SystemLogs';
import SubServiceManagement from './pages/admin/SubServiceManagement';
import OpheServiceManagement from './pages/admin/OpheServiceManagement';
import PaymentSettings from './pages/admin/PaymentSettings';
import MoUManagement from './pages/admin/MoUManagement';
import WebhooksManagement from './pages/admin/WebhooksManagement';


// OPHE Pages
import OphePipeline from './pages/ophe/OphePipeline';
import OpheLeadDetail from './pages/ophe/OpheLeadDetail';

// NotFound Page
import NotFound from './pages/NotFound';

// Helper component to scroll window to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SocketProvider>
          <Router>
            <ScrollToTop />
          <Toaster
            position="top-right"
            containerStyle={{
              zIndex: 999999999,
            }}
            toastOptions={{
              duration: 4000,
              className: 'text-[11px] sm:text-sm font-bold rounded-xl sm:rounded-2xl shadow-2xl border border-white/10 !z-[999999999]',
              style: {
                background: '#1e293b',
                color: '#fff',
                padding: '12px 18px',
                maxWidth: '90vw',
                zIndex: 999999999,
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#22c55e',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 6000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          <Routes>
            {/* Public Routes */}
            <Route path="/mou-sign/:token" element={<MoUSignPage />} />
            <Route path="/payment-status/:id" element={<PaymentStatus />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/onboarding-form/:token" element={<OnboardingForm />} />
            <Route path="/onboarding/:role/:token" element={<OnboardingForm />} />
            <Route path="/mou-consent/:token" element={<MoUConsent />} />
            <Route path="/contact" element={<EnquiryForm />} />
            <Route path="/submit-reports/:id" element={<ClinicalSubmission />} />
            <Route path="/hospital-submit-reports/:id" element={<HospitalClinicalSubmission />} />
            <Route path="/clinical-intake/:id" element={<ClinicalSubmission />} />
            <Route path="/doctor-review/:token" element={<DoctorReview />} />
            <Route path="/surgery-verify/:id" element={<SurgeryVerify />} />
            <Route path="/availability/submit" element={<DoctorAvailabilityPublic />} />
            <Route path="/prescriptions/:id/form" element={<PrescriptionFormPublic />} />

            {/* Protected Dashboard Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
             >
              {/* Dashboard Routes */}
              <Route index element={<HomeRedirect />} />
              <Route path="dashboard" element={<DashboardRouter />} />

              {/* Lead Routes */}
              <Route path="leads" element={<LeadList />} />
              <Route path="leads/create" element={<LeadCreate />} />
              <Route path="leads/reports" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']}>
                  <ReportsList />
                </RoleGuard>
              } />
              <Route path="leads/performance-reports" element={
                <RoleGuard allowedRoles={['admin', 'cc_head']}>
                  <PerformanceReports />
                </RoleGuard>
              } />
              <Route path="leads/register-cc/:id?" element={<LeadRegistrationCC />} />
              <Route path="leads/:id" element={<LeadDetail />} />
              <Route path="schedule-appointment" element={<ScheduleAppointment />} />

              {/* Guidebook Route */}
              <Route path="guidebook" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']}>
                  <GuidebookUI />
                </RoleGuard>
              } />

              {/* Role Flow Dashboard */}
              <Route path="role-flow" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']}>
                  <RoleFlowDashboard />
                </RoleGuard>
              } />

              {/* Surgery Routes */}
              <Route path="surgeries" element={<SurgeryList />} />
              <Route path="surgeries/case-dashboard" element={<SurgeryCaseDashboard />} />
              <Route path="surgeries/test-ui" element={<SurgeryCaseDashboard />} />
              <Route path="surgeries/:id" element={<SurgeryDetail />} />

               {/* Appointment Routes */}
               <Route path="appointments" element={
                 <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head', 'accountant', 'hospital', 'marketing_agent', 'doctor']}>
                   <AppointmentList />
                 </RoleGuard>
               } />
               <Route path="appointments/follow-ups" element={
                 <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head', 'accountant', 'hospital', 'marketing_agent', 'doctor']}>
                   <AppointmentFollowUpList />
                 </RoleGuard>
               } />
               <Route path="appointments/create" element={
                 <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head', 'accountant', 'hospital', 'marketing_agent']}>
                   <AppointmentCreate />
                 </RoleGuard>
               } />
               <Route path="appointments/:id" element={
                 <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head', 'accountant', 'hospital', 'marketing_agent', 'doctor']}>
                   <AppointmentDetail />
                 </RoleGuard>
               } />

               {/* Prescription Routes - doctors can only access the form */}
               <Route path="prescriptions" element={
                 <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head', 'accountant']}>
                   <PrescriptionList />
                 </RoleGuard>
               } />
               <Route path="prescriptions/create" element={
                 <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head', 'accountant']}>
                   <PrescriptionCreate />
                 </RoleGuard>
               } />
                <Route path="prescriptions/:id" element={
                  <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head', 'accountant']}>
                    <PrescriptionDetail />
                  </RoleGuard>
                } />

              <Route path="coupons" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']}>
                  <CouponList />
                </RoleGuard>
              } />

              <Route path="testimonials" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']}>
                  <TestimonialList />
                </RoleGuard>
              } />

              {/* Payment Routes (Admin & Accountant) */}
              <Route path="payments" element={
                <RoleGuard allowedRoles={['admin', 'accountant', 'customer_care', 'hospital', 'cc_head']}>
                  <PaymentList />
                </RoleGuard>
              } />
              <Route path="payments/:id" element={
                <RoleGuard allowedRoles={['admin', 'accountant', 'customer_care', 'hospital', 'cc_head']}>
                  <PaymentDetail />
                </RoleGuard>
              } />


              {/* Follow-up Routes */}
              <Route path="follow-ups" element={<FollowUpList />} />
              <Route path="follow-ups/:id" element={<FollowUpDetail />} />

               {/* Payout Routes */}
               <Route path="payouts/action-center" element={
                 <RoleGuard allowedRoles={['admin', 'accountant', 'cc_head', 'customer_care']}>
                   <AccountantPayoutDashboard />
                 </RoleGuard>
               } />
               <Route path="payouts" element={
                 <RoleGuard allowedRoles={['admin', 'accountant', 'cc_head', 'customer_care', 'doctor']}>
                   <PayoutList />
                 </RoleGuard>
               } />
               <Route path="payouts/:id" element={
                 <RoleGuard allowedRoles={['admin', 'accountant', 'cc_head', 'customer_care', 'doctor']}>
                   <PayoutDetail />
                 </RoleGuard>
               } />

              {/* User Management Routes (Admin Only) */}
              <Route path="users" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']}>
                  <UserList />
                </RoleGuard>
              } />
              <Route path="users/:id" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']}>
                  <UserDetail />
                </RoleGuard>
              } />
              <Route path="roles" element={
                <RoleGuard allowedRoles={['admin']}>
                  <RoleManagement />
                </RoleGuard>
              } />

              <Route path="call-analytics" element={
                <RoleGuard requiredPermission={['call_history:view_all', 'call_history:view_own']}>
                  <CallAnalytics />
                </RoleGuard>
              } />

              {/* Doctor Routes */}
              <Route path="doctors" element={<DoctorList />} />
              <Route path="doctors/:id" element={<DoctorDetail />} />

              {/* Hospital Routes */}
              <Route path="hospitals" element={<HospitalList />} />
              <Route path="hospitals/rate-master" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head', 'accountant']}>
                  <HospitalRateMaster />
                </RoleGuard>
              } />
              <Route path="hospital-rates" element={<Navigate to="/hospitals/rate-master" replace />} />
              <Route path="hospitals/:id" element={<HospitalDetail />} />

              {/* Lead Partner Routes */}
              <Route path="marketing-agents" element={<MarketingAgentList />} />
              <Route path="marketing-agents/:id" element={<MarketingAgentDetail />} />

              {/* OTP Routes */}
              <Route path="otp" element={<OTPList />} />
              <Route path="otp/generate" element={<OTPGenerate />} />
              <Route path="otp/:id" element={<OTPDetail />} />
              <Route path="active-verifications" element={
                <RoleGuard allowedRoles={['doctor', 'hospital']}>
                  <ActiveOTPVerifications />
                </RoleGuard>
              } />

              {/* Doctor Availability Routes */}
              <Route path="doctor-availability" element={<DoctorAvailability />} />
              <Route path="doctor-availability/forecast" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']}>
                  <AvailabilityForecast />
                </RoleGuard>
              } />
              <Route path="doctors/:id/availability" element={<DoctorAvailability />} />

              {/* Profile Route */}
              <Route path="profile" element={<Profile />} />
              <Route path="support" element={<DoxezSupport />} />

              {/* Admin Routes */}
              <Route path="register" element={
                <RoleGuard allowedRoles={['admin']}>
                  <Register />
                </RoleGuard>
              } />
              <Route path="onboarding" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']}>
                  <OnboardingReview />
                </RoleGuard>
              } />
              <Route path="onboarding-requests" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']}>
                  <OnboardingRequests />
                </RoleGuard>
              } />
              <Route path="onboarding/generate" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']}>
                  <OnboardingGenerator />
                </RoleGuard>
              } />
              <Route path="system-logs" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head', 'doctor', 'hospital', 'marketing_agent', 'accountant']}>
                  <SystemLogs />
                </RoleGuard>
              } />
              <Route path="catalog" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']}>
                  <SubServiceManagement />
                </RoleGuard>
              } />
              <Route path="ophe" element={<Navigate to="/ophe/pipeline" replace />} />
              <Route path="ophe/pipeline" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']} requiredPermission={['ophe:view']}>
                  <OphePipeline />
                </RoleGuard>
              } />
              <Route path="ophe/leads" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']} requiredPermission={['ophe:view']}>
                  <OphePipeline />
                </RoleGuard>
              } />
              <Route path="ophe/leads/:id" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']} requiredPermission={['ophe:view']}>
                  <OpheLeadDetail />
                </RoleGuard>
              } />
              <Route path="ophe-services" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']} requiredPermission={['ophe:view']}>
                  <OpheServiceManagement />
                </RoleGuard>
              } />
              <Route path="payment-config" element={
                <RoleGuard allowedRoles={['admin']}>
                  <PaymentSettings />
                </RoleGuard>
              } />
              <Route path="mou-config" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']}>
                  <MoUManagement />
                </RoleGuard>
              } />
              <Route path="webhooks" element={
                <RoleGuard allowedRoles={['admin', 'customer_care', 'cc_head']} requiredPermission="webhook:view">
                  <WebhooksManagement />
                </RoleGuard>
              } />

            </Route>

            {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
      </SocketProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

// Dashboard Router Component
function DashboardRouter() {
  const { user } = useAuth();

  switch (user?.role) {
    case 'admin':
      return <AdminDashboard />;
    case 'doctor':
      return <DoctorDashboard />;
    case 'hospital':
      return <HospitalDashboard />;
    case 'customer_care':
      return <CustomerCareDashboard />;
    case 'cc_head':
      return <CCHeadDashboard />;
    case 'marketing_agent':
      return <MarketingAgentDashboard />;
    case 'accountant':
      return <AccountantDashboard />;
    case 'receptionist':
    case 'staff':
      return <CustomerCareDashboard />;
    default:
      return <AdminDashboard />;
  }
}

function HomeRedirect() {
  return <Navigate to="/dashboard" replace />;
}

export default App;
